import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { courseService } from '../../services/courseService';
import { chapterService, Chapter } from '../../services/chapterService';
import { videoService } from '../../services/videoService';
import { Video } from '../../types';
import { courseTaskService } from '../../services/courseTaskService';
import { Course, Task } from '../../types';
import { useToast } from '../../components/Toast';
import { getMediaUrl } from '../../utils/media';
import { TaskSubmission } from '../../services/taskSubmissionService';

type TabType = 'info' | 'chapters' | 'videos' | 'tasks';

const AdminCourseDetailPage: React.FC = () => {
  const { courseId } = useParams<{ courseId: string }>();
  const navigate = useNavigate();
  const { showToast } = useToast();
  
  const [activeTab, setActiveTab] = useState<TabType>('info');
  const [course, setCourse] = useState<Course | null>(null);
  const [chapters, setChapters] = useState<Chapter[]>([]);
  const [videos, setVideos] = useState<Video[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(false);

  // Form states
  const [editingCourse, setEditingCourse] = useState(false);
  const [courseForm, setCourseForm] = useState({
    title: '',
    description: '',
    price: 0,
    category_id: undefined as number | undefined,
    level: 'beginner' as Course['level'],
    status: 'draft' as Course['status'],
    video_type: 'long_video' as Course['video_type'],
    thumbnail: '',
    experience_points: 0, // Tổng điểm kinh nghiệm có thể nhận được khi hoàn thành khóa học
  });
  const [courseThumbnailFile, setCourseThumbnailFile] = useState<File | null>(null);
  const [courseThumbnailPreview, setCourseThumbnailPreview] = useState<string | null>(null);

  const [newChapter, setNewChapter] = useState({ title: '', description: '', order: 0 });
  const [creatingChapter, setCreatingChapter] = useState(false);
  const [editingChapterId, setEditingChapterId] = useState<number | null>(null);

  const [newVideo, setNewVideo] = useState({
    title: '',
    description: '',
    order: 0,
    chapter_id: '' as number | '',
    videoFile: null as File | null,
    thumbnailFile: null as File | null,
    points: 10, // Điểm kinh nghiệm mặc định khi xem xong video
    slide_url: '',
    imagesText: '',
    slideFile: null as File | null,
    imageFiles: [] as File[],
  });
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [editingVideoId, setEditingVideoId] = useState<number | null>(null);
  const [editingVideoForm, setEditingVideoForm] = useState({
    title: '',
    description: '',
    order: 0,
    chapter_id: '' as number | '',
    videoFile: null as File | null,
    thumbnailFile: null as File | null,
    points: 10, // Điểm kinh nghiệm khi xem xong video
    slide_url: '',
    imagesText: '',
    slideFile: null as File | null,
    imageFiles: [] as File[],
  });
  const [savingVideoId, setSavingVideoId] = useState<number | null>(null);

  const [newTask, setNewTask] = useState({
    title: '',
    description: '',
    points: 0,
    task_type: 'upload' as Task['task_type'],
    video_id: '' as number | '',
    chapter_id: '' as number | '',
    order: 0,
    is_active: true,
  });
  const [creatingTask, setCreatingTask] = useState(false);
  const [editingTaskId, setEditingTaskId] = useState<number | null>(null);
  const [editingTaskForm, setEditingTaskForm] = useState({
    title: '',
    description: '',
    points: 0,
    task_type: 'upload' as Task['task_type'],
    video_id: '' as number | '',
    chapter_id: '' as number | '',
    order: 0,
    is_active: true,
  });
  const [savingTaskId, setSavingTaskId] = useState<number | null>(null);
  const [submissions, setSubmissions] = useState<TaskSubmission[]>([]);
  const [loadingSubmissions, setLoadingSubmissions] = useState(false);
  const [penalizingId, setPenalizingId] = useState<number | null>(null);
  const [reviewingSubmissionId, setReviewingSubmissionId] = useState<number | null>(null);

  useEffect(() => {
    if (courseId) {
      loadCourseData();
    }
  }, [courseId]);

  const loadCourseData = async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const courseData = await courseService.getCourse(Number(courseId));
      setCourse(courseData);
      setCourseForm({
        title: courseData.title,
        description: courseData.description || '',
        price: courseData.price || 0,
        category_id: courseData.category_id || undefined,
        level: courseData.level || 'beginner',
        status: courseData.status,
        video_type: courseData.video_type || 'long_video',
        thumbnail: courseData.thumbnail || '',
        experience_points: courseData.experience_points || 0,
      });
      setCourseThumbnailFile(null);
      setCourseThumbnailPreview(courseData.thumbnail ? getMediaUrl(courseData.thumbnail) : null);
      
      // Load chapters
      const chaptersData = await chapterService.getCourseChapters(Number(courseId));
      setChapters(chaptersData);
      
      // Load videos
      const videosData = await videoService.getCourseVideos(Number(courseId));
      setVideos(videosData);
      
      // Load tasks
      const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
      setTasks(tasksData.tasks);
      await loadSubmissions();
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể tải dữ liệu khóa học', 'error');
      navigate('/admin/courses');
    } finally {
      setLoading(false);
    }
  };

  const loadSubmissions = async () => {
    if (!courseId) return;
    setLoadingSubmissions(true);
    try {
      const { submissions } = await courseTaskService.listSubmissionsAdmin({ course_id: Number(courseId) });
      setSubmissions(submissions);
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể tải danh sách bài nộp', 'error');
    } finally {
      setLoadingSubmissions(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'tasks') {
      loadSubmissions();
    }
  }, [activeTab]);

  const handleUpdateCourse = async () => {
    if (!courseId) return;
    setLoading(true);
    try {
      const response = await courseService.updateCourse(Number(courseId), {
        ...courseForm,
        thumbnailFile: courseThumbnailFile || undefined,
      });
      setCourse(response.course);
      setCourseThumbnailFile(null);
      setCourseThumbnailPreview(response.course.thumbnail ? getMediaUrl(response.course.thumbnail) : null);
      setEditingCourse(false);
      showToast('Cập nhật khóa học thành công', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể cập nhật khóa học', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCourseThumbnailChange = (file: File | null) => {
    setCourseThumbnailFile(file);
    if (file) {
      setCourseForm((prev) => ({ ...prev, thumbnail: '' }));
      const reader = new FileReader();
      reader.onload = (e) => {
        setCourseThumbnailPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setCourseThumbnailPreview(courseForm.thumbnail ? getMediaUrl(courseForm.thumbnail) : null);
    }
  };

  const handleCreateChapter = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId) return;
    setCreatingChapter(true);
    try {
      await chapterService.create({
        course_id: Number(courseId),
        title: newChapter.title,
        description: newChapter.description,
        order: newChapter.order,
      });
      setNewChapter({ title: '', description: '', order: 0 });
      // Reload chapters
      const chaptersData = await chapterService.getCourseChapters(Number(courseId));
      setChapters(chaptersData);
      showToast('Tạo chương thành công', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể tạo chương', 'error');
    } finally {
      setCreatingChapter(false);
    }
  };

  const handleDeleteChapter = async (chapterId: number) => {
    if (!window.confirm('Xóa chương này?')) return;
    try {
      await chapterService.delete(chapterId);
      // Reload chapters and videos
      const chaptersData = await chapterService.getCourseChapters(Number(courseId));
      setChapters(chaptersData);
      const videosData = await videoService.getCourseVideos(Number(courseId));
      setVideos(videosData);
      showToast('Xóa chương thành công', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể xóa chương', 'error');
    }
  };

  const handleDeleteVideo = async (videoId: number) => {
    if (!courseId) return;
    if (!window.confirm('Xóa video này? Tất cả bài tập và bài nộp liên quan cũng sẽ bị xóa.')) return;
    try {
      await videoService.deleteVideo(videoId);
      const videosData = await videoService.getCourseVideos(Number(courseId));
      setVideos(videosData);
      const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
      setTasks(tasksData.tasks);
      showToast('Xóa video thành công', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể xóa video', 'error');
    }
  };

  const startEditVideo = (video: Video) => {
    setEditingVideoId(video.id);
    // Tìm task video_watch cho video này để lấy điểm hiện tại
    const videoWatchTask = tasks.find(t => t.video_id === video.id && t.task_type === 'video_watch');
    setEditingVideoForm({
      title: video.title,
      description: video.description || '',
      order: video.order || 0,
      chapter_id: video.chapter_id || '',
      videoFile: null,
      thumbnailFile: null,
      points: videoWatchTask ? videoWatchTask.points : 10, // Lấy điểm từ task nếu có, mặc định 10
      slide_url: video.slide_url || '',
      imagesText: Array.isArray(video.images) ? video.images.join('\n') : '',
      slideFile: null,
      imageFiles: [],
    });
  };

  const cancelEditVideo = () => {
    setEditingVideoId(null);
    setEditingVideoForm({
      title: '',
      description: '',
      order: 0,
      chapter_id: '',
      videoFile: null,
      thumbnailFile: null,
      points: 10,
      slide_url: '',
      imagesText: '',
      slideFile: null,
      imageFiles: [],
    });
  };

  const handleSaveVideo = async (videoId: number) => {
    if (!courseId) return;
    setSavingVideoId(videoId);
    try {
      const images =
        editingVideoForm.imagesText
          .split('\n')
          .map((s) => s.trim())
          .filter((s) => s.length > 0) || [];

      await videoService.updateVideo(
        videoId,
        {
          title: editingVideoForm.title,
          description: editingVideoForm.description,
          order: editingVideoForm.order,
          chapter_id: editingVideoForm.chapter_id ? Number(editingVideoForm.chapter_id) : undefined,
          videoFile: editingVideoForm.videoFile || undefined,
          thumbnailFile: editingVideoForm.thumbnailFile || undefined,
          slide_url: editingVideoForm.slide_url || '',
          images,
          slideFile: editingVideoForm.slideFile || undefined,
        }
      );
      
      // Cập nhật hoặc tạo task video_watch với điểm kinh nghiệm
      const videoWatchTask = tasks.find(t => t.video_id === videoId && t.task_type === 'video_watch');
      const points = editingVideoForm.points > 0 ? editingVideoForm.points : 10;
      
      if (videoWatchTask) {
        // Cập nhật task hiện có
        await courseTaskService.updateCourseTask(videoWatchTask.id, {
          title: `Xem video: ${editingVideoForm.title}`,
          description: `Hoàn thành video để nhận ${points} điểm kinh nghiệm`,
          points: points,
        } as any);
      } else {
        // Tạo task mới nếu chưa có
        await courseTaskService.createCourseTask({
          title: `Xem video: ${editingVideoForm.title}`,
          description: `Hoàn thành video để nhận ${points} điểm kinh nghiệm`,
          points: points,
          task_type: 'video_watch',
          video_id: videoId,
          chapter_id: editingVideoForm.chapter_id ? Number(editingVideoForm.chapter_id) : undefined,
          order: 0,
          is_active: true,
        } as any);
      }
      
      // Reload videos và tasks
      const videosData = await videoService.getCourseVideos(Number(courseId));
      setVideos(videosData);
      const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
      setTasks(tasksData.tasks);
      
      cancelEditVideo();
      showToast('Cập nhật video và điểm kinh nghiệm thành công', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể cập nhật video', 'error');
    } finally {
      setSavingVideoId(null);
    }
  };

  const handleUploadVideo = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!courseId || !newVideo.videoFile) return;
    setUploadingVideo(true);
    try {
      const images =
        newVideo.imagesText
          .split('\n')
          .map((s) => s.trim())
          .filter((s) => s.length > 0) || [];

      const uploadedVideo = await videoService.uploadVideo(
        Number(courseId),
        newVideo.videoFile,
        {
          title: newVideo.title,
          description: newVideo.description,
          order: newVideo.order,
          chapter_id: newVideo.chapter_id ? Number(newVideo.chapter_id) : undefined,
          thumbnailFile: newVideo.thumbnailFile || undefined,
          slide_url: newVideo.slide_url || '',
          images,
          slideFile: newVideo.slideFile || undefined,
        } as Partial<Video> & { thumbnailFile?: File; slideFile?: File }
      );
      
      // Tự động tạo task "video_watch" với điểm kinh nghiệm
      if (uploadedVideo && uploadedVideo.video && uploadedVideo.video.id) {
        try {
          const points = newVideo.points > 0 ? newVideo.points : 10; // Mặc định 10 điểm nếu không nhập hoặc = 0
          await courseTaskService.createCourseTask({
            title: `Xem video: ${newVideo.title}`,
            description: `Hoàn thành video để nhận ${points} điểm kinh nghiệm`,
            points: points,
            task_type: 'video_watch',
            video_id: uploadedVideo.video.id,
            chapter_id: newVideo.chapter_id ? Number(newVideo.chapter_id) : undefined,
            order: 0,
            is_active: true,
          } as any);
          
          // Reload tasks để cập nhật UI
          const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
          setTasks(tasksData.tasks);
        } catch (taskError: any) {
          // Nếu tạo task thất bại, vẫn hiển thị thành công upload video nhưng cảnh báo
          console.error('Không thể tạo task video_watch:', taskError);
          showToast('Upload video thành công, nhưng không thể tạo task điểm kinh nghiệm. Vui lòng tạo thủ công trong tab "Điểm kinh nghiệm video"', 'warning');
        }
      }
      
      setNewVideo({
        title: '',
        description: '',
        order: 0,
        chapter_id: '',
        videoFile: null,
        thumbnailFile: null,
        points: 10, // Reset về mặc định
        slide_url: '',
        imagesText: '',
        slideFile: null,
        imageFiles: [],
      });
      
      // Reload videos
      const videosData = await videoService.getCourseVideos(Number(courseId));
      setVideos(videosData);
      
      showToast('Upload video thành công và đã tự động gắn điểm kinh nghiệm', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể upload video', 'error');
    } finally {
      setUploadingVideo(false);
    }
  };

  const handleCreateTask = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    // Nếu là task video_watch, bắt buộc phải có video_id
    if (newTask.task_type === 'video_watch' && !newTask.video_id) {
      showToast('Vui lòng chọn video cho nhiệm vụ "Xem video"', 'error');
      return;
    }
    if (newTask.task_type !== 'video_watch' && !newTask.video_id && !newTask.chapter_id) {
      showToast('Vui lòng chọn video hoặc chương', 'error');
      return;
    }
    setCreatingTask(true);
    try {
      const payload: any = {
        title: newTask.title,
        description: newTask.description,
        points: newTask.points,
        task_type: newTask.task_type,
        order: newTask.order,
        is_active: newTask.is_active,
      };
      
      if (newTask.video_id) {
        payload.video_id = Number(newTask.video_id);
      } else if (newTask.chapter_id) {
        payload.chapter_id = Number(newTask.chapter_id);
      }
      
      // Use createVideoTask but with proper typing
      await courseTaskService.createCourseTask(payload as any);
      setNewTask({
        title: '',
        description: '',
        points: 0,
        task_type: 'upload',
        video_id: '',
        chapter_id: '',
        order: 0,
        is_active: true,
      });
      // Reload tasks
      const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
      setTasks(tasksData.tasks);
      showToast('Tạo nhiệm vụ thành công', 'success');
      
      // Nếu là task video_watch, đóng modal và reload videos để cập nhật UI
      if (newTask.task_type === 'video_watch') {
        setNewTask({
          title: '',
          description: '',
          points: 0,
          task_type: 'upload',
          video_id: '',
          chapter_id: '',
          order: 0,
          is_active: true,
        });
      }
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể tạo nhiệm vụ', 'error');
    } finally {
      setCreatingTask(false);
    }
  };

  const startEditTask = (task: Task) => {
    setEditingTaskId(task.id);
    setEditingTaskForm({
      title: task.title,
      description: task.description || '',
      points: task.points,
      task_type: task.task_type || 'upload',
      video_id: task.video_id || '',
      chapter_id: task.chapter_id || '',
      order: task.order || 0,
      is_active: task.is_active ?? true,
    });
  };

  const cancelEditTask = () => {
    setEditingTaskId(null);
    setEditingTaskForm({
      title: '',
      description: '',
      points: 0,
      task_type: 'upload',
      video_id: '',
      chapter_id: '',
      order: 0,
      is_active: true,
    });
  };

  const handleSaveTask = async (taskId: number) => {
    if (!courseId) return;
    setSavingTaskId(taskId);
    try {
      const payload: any = {
        title: editingTaskForm.title,
        description: editingTaskForm.description,
        points: editingTaskForm.points,
        task_type: editingTaskForm.task_type,
        order: editingTaskForm.order,
        is_active: editingTaskForm.is_active,
      };
      if (editingTaskForm.video_id) {
        payload.video_id = Number(editingTaskForm.video_id);
        payload.chapter_id = null;
      } else if (editingTaskForm.chapter_id) {
        payload.chapter_id = Number(editingTaskForm.chapter_id);
        payload.video_id = null;
      } else {
        payload.video_id = null;
        payload.chapter_id = null;
      }
      await courseTaskService.updateCourseTask(taskId, payload);
      const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
      setTasks(tasksData.tasks);
      cancelEditTask();
      showToast('Cập nhật nhiệm vụ thành công', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể cập nhật nhiệm vụ', 'error');
    } finally {
      setSavingTaskId(null);
    }
  };

  const handleDeleteTask = async (taskId: number) => {
    if (!courseId) return;
    if (!window.confirm('Xóa nhiệm vụ này? Các bài nộp liên quan sẽ bị xóa.')) return;
    try {
      await courseTaskService.deleteCourseTask(taskId);
      const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
      setTasks(tasksData.tasks);
      showToast('Xóa nhiệm vụ thành công', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể xóa nhiệm vụ', 'error');
    }
  };

  const handlePenalizeSubmission = async (submission: TaskSubmission) => {
    const defaultPoints = submission.task?.points || 0;
    const pointsInput = window.prompt('Trừ bao nhiêu điểm kinh nghiệm?', String(defaultPoints));
    if (pointsInput === null) return;
    const points = parseInt(pointsInput, 10);
    if (Number.isNaN(points) || points < 0) {
      showToast('Điểm phải là số không âm', 'error');
      return;
    }
    const reason = window.prompt('Lý do (tùy chọn):', submission.feedback || '') || undefined;
    setPenalizingId(submission.id);
    try {
      await courseTaskService.penalizeSubmission(submission.id, { points, reason });
      await loadSubmissions();
      // refresh tasks to reflect status changes
      if (courseId) {
        const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
        setTasks(tasksData.tasks);
      }
      showToast('Đã trừ điểm và đánh dấu gian lận', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể trừ điểm', 'error');
    } finally {
      setPenalizingId(null);
    }
  };

  const handleApproveSubmission = async (submission: TaskSubmission) => {
    setReviewingSubmissionId(submission.id);
    try {
      const result = await courseTaskService.reviewSubmission(submission.id, { status: 'approved' });
      await loadSubmissions();
      // refresh tasks to reflect status changes
      if (courseId) {
        const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
        setTasks(tasksData.tasks);
      }
      const awarded = result.points_awarded || 0;
      showToast(awarded > 0 ? `Đã duyệt bài và cộng ${awarded} điểm kinh nghiệm` : 'Đã đánh dấu bài nộp là hợp lệ', 'success');
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể duyệt bài nộp', 'error');
    } finally {
      setReviewingSubmissionId(null);
    }
  };

  const handleRejectSubmission = async (submission: TaskSubmission) => {
    const reason = window.prompt('Lý do từ chối?', submission.feedback || '');
    if (reason === null) return;
    setReviewingSubmissionId(submission.id);
    try {
      const result = await courseTaskService.reviewSubmission(submission.id, { status: 'rejected', feedback: reason || undefined });
      await loadSubmissions();
      // refresh tasks to reflect status changes
      if (courseId) {
        const tasksData = await courseTaskService.listCourseTasksAdmin({ course_id: Number(courseId) });
        setTasks(tasksData.tasks);
      }
      const deducted = result.points_deducted || 0;
      showToast(
        deducted > 0
          ? `Đã từ chối bài nộp, trừ ${deducted} điểm kinh nghiệm và yêu cầu nộp lại`
          : 'Đã từ chối bài nộp và yêu cầu nộp lại',
        'success'
      );
    } catch (error: any) {
      showToast(error?.response?.data?.error || 'Không thể từ chối bài nộp', 'error');
    } finally {
      setReviewingSubmissionId(null);
    }
  };

  if (loading && !course) {
    return <div className="p-6 text-center">Đang tải...</div>;
  }

  if (!course) {
    return <div className="p-6 text-center">Khóa học không tồn tại</div>;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{course.title}</h1>
          <p className="text-gray-600 mt-1">{course.description}</p>
        </div>
        <button
          onClick={() => navigate('/admin/courses')}
          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          ← Quay lại
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          {(['info', 'chapters', 'videos', 'tasks'] as TabType[]).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab === 'info' && 'Thông tin khóa học'}
              {tab === 'chapters' && 'Chương'}
              {tab === 'videos' && 'Videos'}
              {tab === 'tasks' && 'Nhiệm vụ'}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="bg-white rounded-lg shadow p-6">
        {/* Info Tab */}
        {activeTab === 'info' && (
          <div className="space-y-4">
            <div className="flex justify-between items-center">
              <h2 className="text-xl font-semibold">Thông tin khóa học</h2>
              {!editingCourse && (
                <button
                  onClick={() => setEditingCourse(true)}
                  className="px-4 py-2 bg-primary-600 text-white rounded-lg"
                >
                  Chỉnh sửa
                </button>
              )}
            </div>

            {editingCourse ? (
              <form onSubmit={(e) => { e.preventDefault(); handleUpdateCourse(); }} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="block text-sm font-medium text-gray-700">Ảnh đại diện khóa học</label>
                    {courseThumbnailPreview ? (
                      <img
                        src={courseThumbnailPreview}
                        alt="Course thumbnail"
                        className="w-full max-w-sm rounded-lg border object-cover"
                      />
                    ) : (
                      <div className="w-full max-w-sm h-40 border border-dashed rounded-lg flex items-center justify-center text-gray-400 text-sm">
                        Chưa có ảnh
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => handleCourseThumbnailChange(e.target.files?.[0] || null)}
                        className="border border-gray-300 rounded-lg px-3 py-2"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleCourseThumbnailChange(null)}
                          className="px-3 py-1 border border-gray-300 rounded-lg text-sm"
                        >
                          Xóa ảnh
                        </button>
                      </div>
                      {!courseThumbnailFile && (
                        <input
                          type="text"
                          placeholder="Hoặc dán URL ảnh (tùy chọn)"
                          className="border border-gray-300 rounded-lg px-3 py-2"
                          value={courseForm.thumbnail}
                          onChange={(e) => {
                            setCourseForm({ ...courseForm, thumbnail: e.target.value });
                            setCourseThumbnailPreview(e.target.value ? getMediaUrl(e.target.value) : null);
                          }}
                        />
                      )}
                    </div>
                  </div>
                  <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tiêu đề</label>
                  <input
                    type="text"
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2"
                    value={courseForm.title}
                    onChange={(e) => setCourseForm({ ...courseForm, title: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Mô tả</label>
                  <textarea
                    className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2"
                    rows={4}
                    value={courseForm.description}
                    onChange={(e) => setCourseForm({ ...courseForm, description: e.target.value })}
                  />
                </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Giá</label>
                    <input
                      type="number"
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2"
                      value={courseForm.price}
                      onChange={(e) => setCourseForm({ ...courseForm, price: Number(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cấp độ</label>
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2"
                      value={courseForm.level}
                      onChange={(e) => setCourseForm({ ...courseForm, level: e.target.value as Course['level'] })}
                    >
                      <option value="beginner">Beginner</option>
                      <option value="intermediate">Intermediate</option>
                      <option value="advanced">Advanced</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Trạng thái</label>
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2"
                      value={courseForm.status}
                      onChange={(e) => setCourseForm({ ...courseForm, status: e.target.value as Course['status'] })}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Loại video</label>
                    <select
                      className="mt-1 block w-full border border-gray-300 rounded-lg px-3 py-2"
                      value={courseForm.video_type}
                      onChange={(e) => setCourseForm({ ...courseForm, video_type: e.target.value as Course['video_type'] })}
                    >
                      <option value="long_video">Video dài</option>
                      <option value="short_video">Video ngắn</option>
                    </select>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    type="submit"
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg"
                  >
                    Lưu
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setEditingCourse(false);
                      loadCourseData();
                    }}
                    className="px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    Hủy
                  </button>
                </div>
              </form>
            ) : (
              <div className="space-y-2">
                {course.thumbnail && (
                  <img
                    src={getMediaUrl(course.thumbnail)}
                    alt={course.title}
                    className="w-full max-w-sm rounded-lg border object-cover"
                  />
                )}
                <p><strong>Giá:</strong> {course.price || 0} VNĐ</p>
                <p><strong>⭐ Điểm kinh nghiệm:</strong> {course.experience_points || 0} XP</p>
                <p><strong>Cấp độ:</strong> {course.level}</p>
                <p><strong>Trạng thái:</strong> {course.status}</p>
                <p><strong>Loại video:</strong> {course.video_type === 'long_video' ? 'Video dài' : 'Video ngắn'}</p>
                <p><strong>Số chương:</strong> {chapters.length}</p>
                <p><strong>Số video:</strong> {videos.length}</p>
                <p><strong>Số nhiệm vụ:</strong> {tasks.length}</p>
              </div>
            )}
          </div>
        )}

        {/* Chapters Tab */}
        {activeTab === 'chapters' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Quản lý chương</h2>
            
            {/* Create Chapter Form */}
            <form onSubmit={handleCreateChapter} className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="font-medium">Thêm chương mới</h3>
              <div className="grid grid-cols-3 gap-3">
                <input
                  type="text"
                  placeholder="Tên chương"
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  value={newChapter.title}
                  onChange={(e) => setNewChapter({ ...newChapter, title: e.target.value })}
                  required
                />
                <input
                  type="text"
                  placeholder="Mô tả (tùy chọn)"
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  value={newChapter.description}
                  onChange={(e) => setNewChapter({ ...newChapter, description: e.target.value })}
                />
                <div className="flex gap-2">
                  <input
                    type="number"
                    placeholder="Thứ tự"
                    className="border border-gray-300 rounded-lg px-3 py-2 flex-1"
                    value={newChapter.order}
                    onChange={(e) => setNewChapter({ ...newChapter, order: Number(e.target.value) })}
                  />
                  <button
                    type="submit"
                    disabled={creatingChapter}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                  >
                    {creatingChapter ? 'Đang tạo...' : 'Tạo'}
                  </button>
                </div>
              </div>
            </form>

            {/* Chapters List */}
            <div className="space-y-2">
              {chapters.map((chapter) => (
                <div key={chapter.id} className="border border-gray-200 rounded-lg p-4 flex justify-between items-center">
                  <div>
                    <h4 className="font-medium">{chapter.title}</h4>
                    {chapter.description && <p className="text-sm text-gray-600">{chapter.description}</p>}
                    <p className="text-xs text-gray-500">Thứ tự: {chapter.order} | Videos: {chapter.video_count || 0} | Tasks: {chapter.task_count || 0}</p>
                  </div>
                  <button
                    onClick={() => handleDeleteChapter(chapter.id)}
                    className="px-3 py-1 text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                  >
                    Xóa
                  </button>
                </div>
              ))}
              {chapters.length === 0 && (
                <p className="text-gray-500 text-center py-8">Chưa có chương nào</p>
              )}
            </div>
          </div>
        )}

        {/* Videos Tab */}
        {activeTab === 'videos' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Quản lý video bài giảng</h2>
            
            {/* Upload Video Form */}
            <form onSubmit={handleUploadVideo} className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="font-medium">Upload video mới</h3>
              <div className="grid grid-cols-2 gap-3">
                <input
                  type="text"
                  placeholder="Tiêu đề video"
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  value={newVideo.title}
                  onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                  required
                />
                <select
                  className="border border-gray-300 rounded-lg px-3 py-2"
                  value={newVideo.chapter_id}
                  onChange={(e) => setNewVideo({ ...newVideo, chapter_id: e.target.value ? Number(e.target.value) : '' })}
                >
                  <option value="">-- Chọn chương (tùy chọn) --</option>
                  {chapters.map((ch) => (
                    <option key={ch.id} value={ch.id}>{ch.title}</option>
                  ))}
                </select>
              </div>
              <textarea
                placeholder="Mô tả video"
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                rows={2}
                value={newVideo.description}
                onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
              />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <label className="text-sm text-gray-600 space-y-1">
                  <span className="block mb-1">Link slide bài giảng (PDF / Google Slides)</span>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    value={newVideo.slide_url}
                    onChange={(e) => setNewVideo({ ...newVideo, slide_url: e.target.value })}
                    placeholder="Dán link slide (ví dụ: link Google Slides, PDF trên server, ...)"
                  />
                </label>
                <label className="text-sm text-gray-600 space-y-1">
                  <span className="block mb-1">Danh sách hình ảnh (mỗi dòng 1 URL)</span>
                  <textarea
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                    rows={3}
                    value={newVideo.imagesText}
                    onChange={(e) => setNewVideo({ ...newVideo, imagesText: e.target.value })}
                    placeholder={'https://.../image1.jpg\nhttps://.../image2.png'}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="block text-sm font-semibold text-gray-700">Thứ tự</span>
                  <input
                    type="number"
                    placeholder="0"
                    className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                    value={newVideo.order}
                    onChange={(e) => setNewVideo({ ...newVideo, order: Number(e.target.value) })}
                    min="0"
                  />
                  <p className="text-xs text-gray-500">Số nhỏ hiển thị trước</p>
                </label>
                <label className="space-y-1">
                  <span className="block text-sm font-semibold text-gray-700">
                    ⭐ Điểm kinh nghiệm khi xem xong
                  </span>
                  <input
                    type="number"
                    placeholder="10"
                    className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                    value={newVideo.points}
                    onChange={(e) => setNewVideo({ ...newVideo, points: Number(e.target.value) || 10 })}
                    min="0"
                  />
                  <p className="text-xs text-gray-500">Điểm tự động cấp khi học viên xem xong video (100%)</p>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="block text-sm font-semibold text-gray-700">File video *</span>
                  <input
                    type="file"
                    accept="video/*"
                    className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                    onChange={(e) => setNewVideo({ ...newVideo, videoFile: e.target.files?.[0] || null })}
                    required
                  />
                </label>
                <label className="space-y-1">
                  <span className="block text-sm font-semibold text-gray-700">Ảnh thumbnail</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                    onChange={(e) => setNewVideo({ ...newVideo, thumbnailFile: e.target.files?.[0] || null })}
                  />
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <label className="space-y-1">
                  <span className="block text-sm font-semibold text-gray-700">File slide bài giảng (PDF/PPT, tùy chọn)</span>
                  <input
                    type="file"
                    accept=".pdf,.ppt,.pptx,.key,.odp"
                    className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                    onChange={(e) => setNewVideo({ ...newVideo, slideFile: e.target.files?.[0] || null })}
                  />
                </label>
                <label className="space-y-1">
                  <span className="block text-sm font-semibold text-gray-700">Upload hình ảnh (có thể chọn nhiều)</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                    onChange={(e) =>
                      setNewVideo({
                        ...newVideo,
                        imageFiles: e.target.files ? Array.from(e.target.files) : [],
                      })
                    }
                  />
                </label>
              </div>
              <button
                type="submit"
                disabled={uploadingVideo || !newVideo.videoFile}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
              >
                {uploadingVideo ? 'Đang upload...' : 'Upload video'}
              </button>
            </form>

            {/* Videos List */}
            <div className="space-y-2">
              {videos.map((video) => {
                const chapter = chapters.find(c => c.id === video.chapter_id);
                return (
                  <div key={video.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 pr-4 space-y-3">
                        <div className="flex items-start gap-4">
                          {video.thumbnail ? (
                            <img
                              src={getMediaUrl(video.thumbnail)}
                              alt={video.title}
                              className="w-32 h-20 object-cover rounded-lg border"
                            />
                          ) : video.video_url ? (
                            <video
                              src={getMediaUrl(video.video_url)}
                              controls
                              className="w-32 h-20 object-cover rounded-lg border bg-black"
                            />
                          ) : null
                          }
                          <div className="text-sm text-gray-500">
                            <p className="font-medium text-gray-700 mb-1">Video hiện tại</p>
                            {video.video_url ? (
                              <a
                                href={getMediaUrl(video.video_url)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:underline"
                              >
                                Xem video trong tab mới
                              </a>
                            ) : (
                              <span className="text-gray-400">Chưa có video</span>
                            )}
                          </div>
                        </div>
                        {editingVideoId === video.id ? (
                          <div className="space-y-3">
                            <input
                              type="text"
                              className="w-full border border-gray-300 rounded-lg px-3 py-2"
                              value={editingVideoForm.title}
                              onChange={(e) => setEditingVideoForm({ ...editingVideoForm, title: e.target.value })}
                              placeholder="Tiêu đề video"
                            />
                            <textarea
                              className="w-full border border-gray-300 rounded-lg px-3 py-2"
                              rows={2}
                              value={editingVideoForm.description}
                              onChange={(e) => setEditingVideoForm({ ...editingVideoForm, description: e.target.value })}
                              placeholder="Mô tả video"
                            />
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                              <label className="text-sm text-gray-600 space-y-1">
                                <span className="block mb-1">Link slide bài giảng (PDF / Google Slides)</span>
                                <input
                                  type="text"
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                  value={editingVideoForm.slide_url}
                                  onChange={(e) =>
                                    setEditingVideoForm({ ...editingVideoForm, slide_url: e.target.value })
                                  }
                                  placeholder="Dán link slide (ví dụ: link Google Slides, PDF trên server, ...)"
                                />
                              </label>
                              <label className="text-sm text-gray-600 space-y-1">
                                <span className="block mb-1">Danh sách hình ảnh (mỗi dòng 1 URL)</span>
                                <textarea
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                                  rows={3}
                                  value={editingVideoForm.imagesText}
                                  onChange={(e) =>
                                    setEditingVideoForm({ ...editingVideoForm, imagesText: e.target.value })
                                  }
                                  placeholder={'https://.../image1.jpg\nhttps://.../image2.png'}
                                />
                              </label>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <select
                                className="border border-gray-300 rounded-lg px-3 py-2"
                                value={editingVideoForm.chapter_id || ''}
                                onChange={(e) =>
                                  setEditingVideoForm({
                                    ...editingVideoForm,
                                    chapter_id: e.target.value ? Number(e.target.value) : '',
                                  })
                                }
                              >
                                <option value="">-- Không gắn chương --</option>
                                {chapters.map((ch) => (
                                  <option key={ch.id} value={ch.id}>
                                    {ch.title}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="number"
                                className="border border-gray-300 rounded-lg px-3 py-2"
                                value={editingVideoForm.order}
                                onChange={(e) => setEditingVideoForm({ ...editingVideoForm, order: Number(e.target.value) })}
                                placeholder="Thứ tự"
                              />
                            </div>
                            <label className="space-y-1">
                              <span className="block text-sm font-semibold text-gray-700">
                                ⭐ Điểm kinh nghiệm khi xem xong video
                              </span>
                              <input
                                type="number"
                                placeholder="10"
                                className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                                value={editingVideoForm.points}
                                onChange={(e) => setEditingVideoForm({ ...editingVideoForm, points: Number(e.target.value) || 10 })}
                                min="0"
                              />
                              <p className="text-xs text-gray-500">Điểm tự động cấp khi học viên xem xong video (100%)</p>
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              <label className="text-sm text-gray-600">
                                <span className="block mb-1">Video mới (tùy chọn)</span>
                                <input
                                  type="file"
                                  accept="video/*"
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                  onChange={(e) => setEditingVideoForm({ ...editingVideoForm, videoFile: e.target.files?.[0] || null })}
                                />
                              </label>
                              <label className="text-sm text-gray-600">
                                <span className="block mb-1">Ảnh thumbnail mới (tùy chọn)</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                  onChange={(e) => setEditingVideoForm({ ...editingVideoForm, thumbnailFile: e.target.files?.[0] || null })}
                                />
                              </label>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <label className="text-sm text-gray-600">
                                <span className="block mb-1">File slide bài giảng mới (PDF/PPT, tùy chọn)</span>
                                <input
                                  type="file"
                                  accept=".pdf,.ppt,.pptx,.key,.odp"
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                  onChange={(e) =>
                                    setEditingVideoForm({ ...editingVideoForm, slideFile: e.target.files?.[0] || null })
                                  }
                                />
                              </label>
                              <label className="text-sm text-gray-600">
                                <span className="block mb-1">Upload hình ảnh mới (có thể chọn nhiều)</span>
                                <input
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                  onChange={(e) =>
                                    setEditingVideoForm({
                                      ...editingVideoForm,
                                      imageFiles: e.target.files ? Array.from(e.target.files) : [],
                                    })
                                  }
                                />
                              </label>
                            </div>
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleSaveVideo(video.id)}
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                                disabled={savingVideoId === video.id}
                              >
                                {savingVideoId === video.id ? 'Đang lưu...' : 'Lưu'}
                              </button>
                              <button
                                onClick={cancelEditVideo}
                                className="px-4 py-2 border border-gray-300 rounded-lg"
                              >
                                Hủy
                              </button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <h4 className="font-medium">{video.title}</h4>
                            {video.description && <p className="text-sm text-gray-600">{video.description}</p>}
                            <p className="text-xs text-gray-500">
                              Chương: {chapter?.title || 'Không có'} | Thứ tự: {video.order} | Tasks: {video.task_count || 0}
                            </p>
                          </>
                        )}
                      </div>
                      {editingVideoId !== video.id && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => startEditVideo(video)}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Sửa
                          </button>
                          <button
                            onClick={() => handleDeleteVideo(video.id)}
                            className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                          >
                            Xóa
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              {videos.length === 0 && (
                <p className="text-gray-500 text-center py-8">Chưa có video nào</p>
              )}
            </div>
          </div>
        )}

        {/* Tasks Tab */}
        {activeTab === 'tasks' && (
          <div className="space-y-4">
            <h2 className="text-xl font-semibold">Quản lý nhiệm vụ</h2>
            
            {/* Create Task Form */}
            <form onSubmit={handleCreateTask} className="bg-gray-50 p-4 rounded-lg space-y-3">
              <h3 className="font-medium">Thêm nhiệm vụ mới</h3>
              <div className="grid grid-cols-2 gap-3">
                <label className="text-sm text-gray-600 space-y-1">
                  <span>Tiêu đề nhiệm vụ *</span>
                  <input
                    type="text"
                    placeholder={newTask.task_type === 'video_watch' ? 'VD: Xem video bài giảng #1' : 'VD: Bài tập video #1'}
                    className="border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                    value={newTask.title}
                    onChange={(e) => setNewTask({ ...newTask, title: e.target.value })}
                    required
                  />
                </label>
                <label className="text-sm text-gray-600 space-y-1">
                  <span>Điểm thưởng (XP) *</span>
                  <input
                    type="number"
                    placeholder="0"
                    className="border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                    value={newTask.points}
                    onChange={(e) => setNewTask({ ...newTask, points: Number(e.target.value) })}
                    required
                    min="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {newTask.task_type === 'video_watch' 
                      ? '⭐ Số điểm người học tự động nhận được khi xem xong video (100%).' 
                      : 'Số điểm người học nhận được khi hoàn thành.'}
                  </p>
                </label>
              </div>
              <textarea
                placeholder={newTask.task_type === 'video_watch' ? 'Mô tả (tùy chọn): Ví dụ: Xem hết video để nhận điểm kinh nghiệm' : 'Mô tả nhiệm vụ'}
                className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                rows={2}
                value={newTask.description}
                onChange={(e) => setNewTask({ ...newTask, description: e.target.value })}
              />
              <div className="grid grid-cols-3 gap-3">
                <label className="text-sm text-gray-600 space-y-1">
                  <span>Loại nhiệm vụ *</span>
                  <select
                    className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-bold outline-none"
                    value={newTask.task_type}
                    onChange={(e) => setNewTask({ ...newTask, task_type: e.target.value as Task['task_type'] })}
                  >
                    <option value="upload">Upload bài tập</option>
                    <option value="video_watch">Xem video (tự động cấp điểm khi xem xong)</option>
                    <option value="custom">Tùy chỉnh</option>
                  </select>
                </label>
                <label className="text-sm text-gray-600 space-y-1">
                  <span>Chọn video {newTask.task_type === 'video_watch' ? '*' : '(tùy chọn)'}</span>
                  <select
                    className="w-full border-2 border-red-300 rounded-xl px-4 py-3 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-bold outline-none"
                    value={newTask.video_id || ''}
                    onChange={(e) => {
                      const videoId = e.target.value ? Number(e.target.value) : '';
                      setNewTask({ ...newTask, video_id: videoId, chapter_id: videoId ? videos.find(v => v.id === videoId)?.chapter_id || '' : newTask.chapter_id });
                    }}
                    required={newTask.task_type === 'video_watch'}
                  >
                    <option value="">-- Chọn video --</option>
                    {videos.map((v) => (
                      <option key={v.id} value={v.id}>{v.title}</option>
                    ))}
                  </select>
                  {newTask.task_type === 'video_watch' && (
                    <p className="text-xs text-red-600 font-semibold mt-1">⚠️ Bắt buộc chọn video cho loại nhiệm vụ này</p>
                  )}
                </label>
                <div className="flex gap-4 items-end flex-wrap">
                  <label className="text-sm text-gray-600 space-y-1 flex-1 min-w-[140px]">
                    <span>Thứ tự hiển thị</span>
                    <input
                      type="number"
                      placeholder="0"
                      className="border border-gray-300 rounded-lg px-3 py-2 w-full"
                      value={newTask.order}
                      onChange={(e) => setNewTask({ ...newTask, order: Number(e.target.value) })}
                    />
                    <p className="text-xs text-gray-500">0 = đầu danh sách. Số nhỏ xuất hiện trước.</p>
                  </label>
                  <label className="text-sm text-gray-600 space-y-1 w-40">
                    <span>Trạng thái</span>
                    <select
                      className="border border-gray-300 rounded-lg px-3 py-2 w-full"
                      value={newTask.is_active ? 'true' : 'false'}
                      onChange={(e) => setNewTask({ ...newTask, is_active: e.target.value === 'true' })}
                    >
                      <option value="true">Hiển thị ngay</option>
                      <option value="false">Ẩn tạm thời</option>
                    </select>
                  </label>
                  <button
                    type="submit"
                    disabled={creatingTask}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg disabled:opacity-50"
                  >
                    {creatingTask ? 'Đang tạo...' : 'Tạo'}
                  </button>
                </div>
              </div>
              {!newTask.video_id && (
                <select
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  value={newTask.chapter_id || ''}
                  onChange={(e) => setNewTask({ ...newTask, chapter_id: e.target.value ? Number(e.target.value) : '', video_id: '' })}
                >
                  <option value="">-- Hoặc chọn chương --</option>
                  {chapters.map((ch) => (
                    <option key={ch.id} value={ch.id}>{ch.title}</option>
                  ))}
                </select>
              )}
            </form>

            {/* Tasks List */}
            <div className="space-y-2">
              {tasks.length === 0 ? (
                <p className="text-gray-500 text-center py-8">Chưa có nhiệm vụ nào</p>
              ) : (
                tasks.map((task) => {
                const video = videos.find(v => v.id === task.video_id);
                const chapter = chapters.find(c => c.id === task.chapter_id);
                return (
                  <div key={task.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1 pr-4 space-y-3">
                        {editingTaskId === task.id ? (
                          <div className="space-y-3">
                            {task.task_type === 'video_watch' ? (
                              // Edit form for video_watch task (simplified)
                              <div className="space-y-4 p-4 bg-red-50 rounded-xl border-2 border-red-200">
                                <label className="space-y-2 block">
                                  <span className="text-base font-extrabold text-gray-900">Tiêu đề *</span>
                                  <input
                                    type="text"
                                    className="w-full border-3 border-red-300 rounded-xl px-4 py-3.5 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                                    value={editingTaskForm.title}
                                    onChange={(e) => setEditingTaskForm({ ...editingTaskForm, title: e.target.value })}
                                    placeholder="Tiêu đề nhiệm vụ"
                                    required
                                  />
                                </label>
                                <label className="space-y-2 block">
                                  <span className="text-base font-extrabold text-gray-900">Điểm kinh nghiệm (XP) *</span>
                                  <input
                                    type="number"
                                    className="w-full border-3 border-red-300 rounded-xl px-4 py-3.5 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                                    value={editingTaskForm.points}
                                    onChange={(e) => setEditingTaskForm({ ...editingTaskForm, points: Number(e.target.value) })}
                                    placeholder="10"
                                    required
                                    min="0"
                                  />
                                  <p className="text-xs text-gray-600 font-semibold">
                                    ⭐ Số điểm học viên tự động nhận được khi xem xong video (100%)
                                  </p>
                                </label>
                                <label className="space-y-2 block">
                                  <span className="text-base font-extrabold text-gray-900">Mô tả (tùy chọn)</span>
                                  <textarea
                                    className="w-full border-3 border-red-300 rounded-xl px-4 py-3.5 text-base focus:ring-4 focus:ring-red-400 focus:border-red-500 transition-all font-semibold outline-none"
                                    rows={2}
                                    value={editingTaskForm.description}
                                    onChange={(e) => setEditingTaskForm({ ...editingTaskForm, description: e.target.value })}
                                    placeholder="Mô tả nhiệm vụ"
                                  />
                                </label>
                              </div>
                            ) : (
                              // Edit form for other task types
                              <>
                                <input
                                  type="text"
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                  value={editingTaskForm.title}
                                  onChange={(e) => setEditingTaskForm({ ...editingTaskForm, title: e.target.value })}
                                  placeholder="Tiêu đề nhiệm vụ"
                                />
                                <textarea
                                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                                  rows={2}
                                  value={editingTaskForm.description}
                                  onChange={(e) => setEditingTaskForm({ ...editingTaskForm, description: e.target.value })}
                                  placeholder="Mô tả nhiệm vụ"
                                />
                                <div className="grid grid-cols-2 gap-3">
                                  <select
                                    className="border border-gray-300 rounded-lg px-3 py-2"
                                    value={editingTaskForm.task_type}
                                    onChange={(e) =>
                                      setEditingTaskForm({ ...editingTaskForm, task_type: e.target.value as Task['task_type'] })
                                    }
                                  >
                                    <option value="upload">Upload bài tập</option>
                                    <option value="video_watch">Xem video (tự động cấp điểm khi xem xong)</option>
                                    <option value="custom">Tùy chỉnh</option>
                                  </select>
                                  <input
                                    type="number"
                                    className="border border-gray-300 rounded-lg px-3 py-2"
                                    value={editingTaskForm.points}
                                    onChange={(e) => setEditingTaskForm({ ...editingTaskForm, points: Number(e.target.value) })}
                                    placeholder="Điểm"
                                  />
                                </div>
                              </>
                            )}
                            <div className="grid grid-cols-2 gap-3">
                              <select
                                className="border border-gray-300 rounded-lg px-3 py-2"
                                value={editingTaskForm.video_id || ''}
                                onChange={(e) => {
                                  const videoId = e.target.value ? Number(e.target.value) : '';
                                  setEditingTaskForm({
                                    ...editingTaskForm,
                                    video_id: videoId,
                                    chapter_id: videoId ? videos.find(v => v.id === videoId)?.chapter_id || '' : editingTaskForm.chapter_id,
                                  });
                                }}
                              >
                                <option value="">-- Chọn video --</option>
                                {videos.map((v) => (
                                  <option key={v.id} value={v.id}>
                                    {v.title}
                                  </option>
                                ))}
                              </select>
                              <select
                                className="border border-gray-300 rounded-lg px-3 py-2"
                                value={editingTaskForm.chapter_id || ''}
                                onChange={(e) =>
                                  setEditingTaskForm({
                                    ...editingTaskForm,
                                    chapter_id: e.target.value ? Number(e.target.value) : '',
                                    video_id: '',
                                  })
                                }
                                disabled={!!editingTaskForm.video_id}
                              >
                                <option value="">-- Hoặc chọn chương --</option>
                                {chapters.map((ch) => (
                                  <option key={ch.id} value={ch.id}>
                                    {ch.title}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                              <input
                                type="number"
                                className="border border-gray-300 rounded-lg px-3 py-2"
                                value={editingTaskForm.order}
                                onChange={(e) => setEditingTaskForm({ ...editingTaskForm, order: Number(e.target.value) })}
                                placeholder="Thứ tự"
                              />
                              <select
                                className="border border-gray-300 rounded-lg px-3 py-2"
                                value={editingTaskForm.is_active ? 'true' : 'false'}
                                onChange={(e) => setEditingTaskForm({ ...editingTaskForm, is_active: e.target.value === 'true' })}
                              >
                                <option value="true">Kích hoạt</option>
                                <option value="false">Ẩn</option>
                              </select>
                            </div>
                            <div className="flex gap-2">
                              <div className="flex gap-3">
                                <button
                                  onClick={() => handleSaveTask(task.id)}
                                  className="flex-1 px-5 py-3 rounded-xl bg-gradient-to-r from-green-500 to-green-600 text-white font-extrabold hover:from-green-600 hover:to-green-700 transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-1 active:translate-y-0 border-2 border-green-400 disabled:opacity-50"
                                  disabled={savingTaskId === task.id}
                                >
                                  {savingTaskId === task.id ? 'Đang lưu...' : 'Lưu'}
                                </button>
                                <button
                                  onClick={cancelEditTask}
                                  className="px-5 py-3 rounded-xl bg-gray-200 text-gray-800 font-extrabold hover:bg-gray-300 transition-all border-2 border-gray-400"
                                >
                                  Hủy
                                </button>
                              </div>
                            </div>
                          </div>
                        ) : (
                          <>
                            <h4 className="font-medium">{task.title}</h4>
                            {task.description && <p className="text-sm text-gray-600">{task.description}</p>}
                            <p className="text-xs text-gray-500">
                              {task.video_id ? `Video: ${video?.title || 'N/A'}` : `Chương: ${chapter?.title || 'N/A'}`} | Điểm: {task.points} | Thứ tự: {task.order} |{' '}
                              {task.is_active ? 'Đang hiển thị' : 'Đang ẩn'}
                            </p>
                          </>
                        )}
                      </div>
                      {editingTaskId !== task.id && (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => startEditTask(task)}
                            className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Sửa
                          </button>
                          <button
                            onClick={() => handleDeleteTask(task.id)}
                            className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                          >
                            Xóa
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                );
                })
              )}
            </div>

            <div className="pt-6 border-t border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold">Bài nộp gần đây</h3>
                <button
                  onClick={loadSubmissions}
                  className="px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Làm mới
                </button>
              </div>
              {loadingSubmissions ? (
                <div className="text-center py-8 text-gray-500">Đang tải danh sách bài nộp...</div>
              ) : submissions.length === 0 ? (
                <div className="text-gray-500 py-6 text-center">Chưa có bài nộp nào cho khóa học này.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead>
                      <tr className="text-left text-gray-500 uppercase text-xs tracking-wide border-b border-gray-100">
                        <th className="py-2 pr-4">Học viên</th>
                        <th className="py-2 pr-4">Video</th>
                        <th className="py-2 pr-4">Nhiệm vụ</th>
                        <th className="py-2 pr-4">Trạng thái</th>
                        <th className="py-2 pr-4">Nộp lúc</th>
                        <th className="py-2 pr-4">Minh chứng</th>
                        <th className="py-2 pr-4">Phản hồi</th>
                        <th className="py-2 pr-4 text-right">Hành động</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map((submission) => (
                        <tr key={submission.id} className="border-b border-gray-50">
                          <td className="py-2 pr-4">
                            <div className="font-medium text-gray-900">{submission.user?.full_name || submission.user?.username || 'Ẩn danh'}</div>
                            <div className="text-xs text-gray-500">ID: {submission.user_id}</div>
                          </td>
                          <td className="py-2 pr-4">
                            <div className="text-gray-900">{submission.video?.title || submission.task?.title}</div>
                            <div className="text-xs text-gray-500">Video #{submission.video?.id || submission.task?.video_id}</div>
                          </td>
                          <td className="py-2 pr-4">
                            <div className="text-gray-900">{submission.task?.title}</div>
                            <div className="text-xs text-gray-500">{submission.task?.points || 0} điểm</div>
                          </td>
                          <td className="py-2 pr-4">
                            {submission.status === 'approved' && (
                              <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-semibold">Đã duyệt</span>
                            )}
                            {submission.status === 'pending' && (
                              <span className="px-2 py-1 bg-yellow-50 text-yellow-700 rounded-full text-xs font-semibold">Chờ duyệt</span>
                            )}
                            {submission.status === 'rejected' && (
                              <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full text-xs font-semibold">Bị từ chối</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-sm text-gray-600">
                            {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : '--'}
                          </td>
                          <td className="py-2 pr-4">
                            {submission.submission_file ? (
                              <a
                                href={getMediaUrl(submission.submission_file)}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-primary-600 hover:underline"
                              >
                                Tải file
                              </a>
                            ) : submission.submission_text ? (
                              <div className="text-xs italic text-gray-600 max-w-xs truncate">{submission.submission_text}</div>
                            ) : (
                              <span className="text-gray-400 text-xs">Không có</span>
                            )}
                          </td>
                          <td className="py-2 pr-4 text-xs text-gray-600 max-w-xs">
                            {submission.feedback || (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                          <td className="py-2 pl-4 text-right space-x-2 whitespace-nowrap">
                            {submission.status !== 'approved' && (
                              <button
                                onClick={() => handleApproveSubmission(submission)}
                                className="px-3 py-1 text-sm border border-green-200 text-green-700 rounded-lg hover:bg-green-50"
                                disabled={reviewingSubmissionId === submission.id}
                              >
                                {reviewingSubmissionId === submission.id ? 'Đang duyệt...' : 'Duyệt'}
                              </button>
                            )}
                            {submission.status !== 'rejected' && (
                              <button
                                onClick={() => handleRejectSubmission(submission)}
                                className="px-3 py-1 text-sm border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-50"
                                disabled={reviewingSubmissionId === submission.id}
                              >
                                {reviewingSubmissionId === submission.id ? 'Đang xử lý...' : 'Từ chối'}
                              </button>
                            )}
                            <button
                              onClick={() => handlePenalizeSubmission(submission)}
                              className="px-3 py-1 text-sm text-red-600 border border-red-300 rounded-lg hover:bg-red-50"
                              disabled={penalizingId === submission.id}
                            >
                              {penalizingId === submission.id ? 'Đang trừ...' : 'Trừ điểm'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-3">
                * Tất cả bài nộp được duyệt tự động để học viên tiếp tục bài mới. Quản trị viên có thể trừ điểm và khóa nhiệm vụ nếu phát hiện gian lận.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCourseDetailPage;

